import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "./div-modal.scss";
import CloseIcon from "../../SVG/close";

export const DivModalPortalContext =
  React.createContext<React.RefObject<HTMLElement | null> | null>(null);

/** Ref to the portal mount inside an open DivModal. Pass to `portalTarget` on portaled children. */
export function useDivModalPortalTarget():
  | React.RefObject<HTMLElement | null>
  | undefined {
  return React.useContext(DivModalPortalContext) ?? undefined;
}

interface DivModalProps {
  dialogId: string;
  onSave: () => void | Promise<void>;
  children: React.ReactNode;
  title: string;
  /** Optional content rendered in the header after the title. */
  headerContent?: React.ReactNode;
  saveButtonText: string;
  onCancel?: () => void | Promise<void>;
  onClose?: () => void | Promise<void>;
  cancelText: string;
  heightPercentage?: number;
  /**
   * Auto-fit modal to its content, capping the height at this percentage of the viewport.
   * When set, the modal stays compact for short content and scrolls inside the body
   * once content exceeds the cap. Takes precedence over `heightPercentage`.
   */
  maxHeightPercentage?: number;
  showSingleButton?: boolean;
  disableSave?: boolean;
  hideFooter?: boolean;
  autoCloseOnSave?: boolean;
  disableOnCloseFunc?: boolean;
  hideCloseButton?: boolean;
  disableCloseOnOutsideClick?: boolean;
}

export function DivModal(props: DivModalProps) {
  const [mounted, setMounted] = useState(false);
  const portalRootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleCancel = () => {
    if (!props.disableOnCloseFunc) {
      closeDivModal(props.dialogId, props.onClose);
    }
    if (props.onCancel) {
      props.onCancel();
    }
  };

  useEffect(() => {
    const wrapperEle = document.getElementById(props.dialogId);

    const handleClickOutside = (event: MouseEvent) => {
      if (event.target === wrapperEle) {
        closeDivModal(props.dialogId, props.onClose);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (props.disableCloseOnOutsideClick) {
          event.preventDefault();
        } else if (wrapperEle?.classList.contains("show")) {
          closeDivModal(props.dialogId, props.onClose);
        }
      }
    };

    if (!props.disableCloseOnOutsideClick) {
      wrapperEle?.addEventListener("click", handleClickOutside);
    }
    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      wrapperEle?.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [props.dialogId, props.disableCloseOnOutsideClick, mounted]);

  const isAutoFit = props.maxHeightPercentage != null;
  const cappedPct = props.maxHeightPercentage ?? props.heightPercentage;

  const modalNode = (
    <div id={props.dialogId} className="custom-div-modal">
      <DivModalPortalContext.Provider value={portalRootRef}>
        <div
          ref={portalRootRef}
          className="custom-div-modal__portal-root"
          aria-hidden="true"
        />
        <div
          className={`custom-div-modal__panel${isAutoFit ? " custom-div-modal__panel--auto-fit" : ""}`}
          style={{
            height: isAutoFit ? "fit-content" : "auto",
            maxHeight: cappedPct ? `${cappedPct}%` : undefined,
          }}
        >
          <div className="custom-div-modal-container">
            <div className="dialog-header">
              <div className="dialog-header-main">
                <div className="dialog-header-title">{props.title}</div>
                {props.headerContent != null && (
                  <div className="dialog-header-content">{props.headerContent}</div>
                )}
              </div>
              {!props.hideCloseButton && (
                <div
                  className="dialog-close-button"
                  onClick={() => closeDivModal(props.dialogId, props.onClose)}
                >
                  <CloseIcon />
                </div>
              )}
            </div>
            <div className="dialog-body">{props.children}</div>
            {!props.hideFooter && (
              <div className="dialog-footer">
                {props.showSingleButton ? (
                  <button
                    className="dialog-footer-button save"
                    onClick={props.onSave}
                    disabled={props.disableSave}
                  >
                    {props.saveButtonText}
                  </button>
                ) : (
                  <>
                    <button
                      className="dialog-footer-button cancel"
                      onClick={handleCancel}
                    >
                      {props.cancelText}
                    </button>
                    <button
                      className="dialog-footer-button save"
                      onClick={props.onSave}
                      disabled={props.disableSave}
                    >
                      {props.saveButtonText}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </DivModalPortalContext.Provider>
    </div>
  );

  if (!mounted || typeof document === "undefined") {
    return null;
  }

  return ReactDOM.createPortal(modalNode, document.body);
}

export function openDivModal(arg0: string): void {
  const wrapperEle = document.getElementById(arg0);
  if (!wrapperEle) return;
  wrapperEle.classList.add("show");
}

export function closeDivModal(arg0: string, onCloseFunc?: any): void {
  const wrapperEle = document.getElementById(arg0);
  if (!wrapperEle) return;
  if (onCloseFunc) {
    onCloseFunc();
  }
  wrapperEle.classList.remove("show");
}
